/**
 * Generate rich, crawler‑friendly text for the SRP location header.
 *
 * This is purely data-in → text-out logic so it can be:
 * - Reused from the React layer
 * - Fully unit‑tested
 */

import { extractCompanySuffix, capitalizeCompanyName } from '../company/company-name';

export type SupportedLocale = 'en' | 'fi' | 'sv';

export interface LocationHeaderMetrics {
  location: string;
  locale: SupportedLocale;
  nbHits: number;
  sampleCount: number;
  remotePercentage: number;
  avgSalaryNum: number; // raw cents/month, may be 0 when unknown
  avgSalaryFormatted: string; // e.g. "€4k"
  languageTotals: {
    english: number;
    local: number;
    total: number;
  };
  /**
   * Top employers for this location, sorted by volume (most jobs first).
   * Pass an empty array when we don't have company distribution data.
   */
  topCompanies: string[];
  /**
   * Top industries/fields for this location, sorted by volume (most jobs first).
   * Pass an empty array when we don't have field distribution data.
   */
  topIndustries: string[];
  /**
   * Finland-wide averages for comparison (optional).
   * When provided, we compare location metrics to these national averages.
   */
  finlandAverages?: {
    avgSalaryNum?: number; // raw cents/month
    avgSalaryFormatted?: string; // e.g. "€4k"
    remotePercentage?: number;
    englishLanguagePercentage?: number;
  };
}

export interface LocationHeaderText {
  overviewText: string;
  salaryBlurb: string;
  workModelBlurb: string;
  languageBlurb: string;
}

function formatPercent(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.round(value);
}

function formatJobsCount(nbHits: number, locale: SupportedLocale): string {
  if (!Number.isFinite(nbHits) || nbHits <= 0) return '';
  const localeString = locale === 'fi' ? 'fi-FI' : locale === 'sv' ? 'sv-SE' : 'en-US';
  return nbHits.toLocaleString(localeString);
}

/**
 * Clean company name by extracting main name (stripping suffix) and capitalizing properly.
 * Uses the same logic as CompanyName component.
 */
function cleanCompanyName(name: string): string {
  if (!name || typeof name !== 'string') return name || '';
  const { mainName } = extractCompanySuffix(name.trim());
  // Apply proper title case capitalization
  return capitalizeCompanyName(mainName);
}

function buildOverviewEn(metrics: LocationHeaderMetrics): string {
  const parts: string[] = [];

  // Paragraph 1 – overall market summary
  if (metrics.location) {
    const companies = (metrics.topCompanies || []).filter(Boolean).map(cleanCompanyName);
    const industries = (metrics.topIndustries || []).filter(Boolean).slice(0, 3);

    if (companies.length > 0 || industries.length > 0) {
      let employersPhrase = '';
      if (companies.length > 0) {
        if (companies.length === 1) {
          employersPhrase = companies[0];
        } else if (companies.length === 2) {
          employersPhrase = `${companies[0]} and ${companies[1]}`;
        } else {
          employersPhrase = `${companies[0]}, ${companies[1]} and ${companies[2]}`;
        }
      }

      let industriesPhrase = '';
      if (industries.length > 0) {
        if (industries.length === 1) {
          industriesPhrase = industries[0];
        } else if (industries.length === 2) {
          industriesPhrase = `${industries[0]} and ${industries[1]}`;
        } else {
          industriesPhrase = `${industries[0]}, ${industries[1]} and ${industries[2]}`;
        }
      }

      if (employersPhrase && industriesPhrase) {
        parts.push(
          `The ${metrics.location} job market on JobCrawls currently features live job ads, ` +
            `with the largest employers including ${employersPhrase}, and top industries spanning ${industriesPhrase} and other sectors.`
        );
      } else if (employersPhrase) {
        parts.push(
          `The ${metrics.location} job market on JobCrawls currently features live job ads, ` +
            `with the largest employers including ${employersPhrase} and many other hiring companies.`
        );
      } else if (industriesPhrase) {
        parts.push(
          `The ${metrics.location} job market on JobCrawls currently features live job ads, ` +
            `with top industries spanning ${industriesPhrase} and other sectors.`
        );
      } else {
        parts.push(
          `The ${metrics.location} job market on JobCrawls currently features live job ads ` +
            `across technology, healthcare, services and many other industries.`
        );
      }
    } else {
      parts.push(
        `The ${metrics.location} job market on JobCrawls currently features live job ads ` +
          `across technology, healthcare, services and many other industries.`
      );
    }
  }

  // Paragraph 2 – salary + remote (with Finland comparison)
  const salaryAvailable = metrics.avgSalaryNum > 0;
  const remotePct = formatPercent(metrics.remotePercentage);
  const finlandAvg = metrics.finlandAverages;

  if (salaryAvailable || remotePct > 0) {
    let salaryPart = '';
    if (salaryAvailable) {
      if (finlandAvg?.avgSalaryNum && finlandAvg.avgSalaryNum > 0) {
        const diff = metrics.avgSalaryNum - finlandAvg.avgSalaryNum;
        const diffPercent = Math.round((diff / finlandAvg.avgSalaryNum) * 100);
        if (Math.abs(diffPercent) >= 5) {
          if (diffPercent > 0) {
            salaryPart = `Average advertised salaries in ${metrics.location} are around ${metrics.avgSalaryFormatted} per month, which is approximately ${Math.abs(diffPercent)}% higher than the Finland average of ${finlandAvg.avgSalaryFormatted}`;
          } else {
            salaryPart = `Average advertised salaries in ${metrics.location} are around ${metrics.avgSalaryFormatted} per month, which is approximately ${Math.abs(diffPercent)}% lower than the Finland average of ${finlandAvg.avgSalaryFormatted}`;
          }
        } else {
          salaryPart = `Average advertised salaries in ${metrics.location} are around ${metrics.avgSalaryFormatted} per month for full‑time roles, similar to the Finland average`;
        }
      } else {
        salaryPart = `Average advertised salaries in this area are around ${metrics.avgSalaryFormatted} per month for full‑time roles`;
      }
    }

    let remotePart = '';
    if (remotePct > 0) {
      if (finlandAvg?.remotePercentage !== undefined && finlandAvg.remotePercentage > 0) {
        const diff = remotePct - finlandAvg.remotePercentage;
        const diffPercent = Math.round((diff / finlandAvg.remotePercentage) * 100);
        if (Math.abs(diffPercent) >= 10) {
          if (diffPercent > 0) {
            remotePart = `${salaryAvailable ? ', with ' : 'There is '}roughly ${remotePct}% of roles offering remote or hybrid work, which is significantly higher than the Finland average of ${formatPercent(finlandAvg.remotePercentage)}%`;
          } else {
            remotePart = `${salaryAvailable ? ', with ' : 'There is '}roughly ${remotePct}% of roles offering remote or hybrid work, which is lower than the Finland average of ${formatPercent(finlandAvg.remotePercentage)}%`;
          }
        } else {
          remotePart = `${salaryAvailable ? ', with ' : 'There is '}roughly ${remotePct}% of roles offering remote or hybrid work, similar to the Finland average`;
        }
      } else {
        if (remotePct >= 60) {
          remotePart = `${salaryAvailable ? ', with ' : 'There is also '}roughly ${remotePct}% of roles offering remote or hybrid work options`;
        } else if (remotePct >= 30) {
          remotePart = `${salaryAvailable ? ', and about ' : 'Around '}${remotePct}% of roles offer some form of remote or hybrid flexibility`;
        } else {
          remotePart = `${salaryAvailable ? ', and a smaller share of roughly ' : 'Only around '}${remotePct}% of roles are remote or hybrid`;
        }
      }
    }

    if (salaryPart || remotePart) {
      parts.push(`${salaryPart}${remotePart ? `${salaryPart ? ' ' : ''}${remotePart}.` : '.'}`.trim());
    }
  }

  // Paragraph 3 – languages (with Finland comparison)
  const { english, local, total } = metrics.languageTotals;
  if (total > 0) {
    const englishPct = formatPercent((english / total) * 100);
    const localPct = Math.max(0, 100 - englishPct);
    const finlandAvg = metrics.finlandAverages;

    if (finlandAvg?.englishLanguagePercentage !== undefined && finlandAvg.englishLanguagePercentage > 0) {
      const diff = englishPct - finlandAvg.englishLanguagePercentage;
      const diffPercent = Math.round((diff / finlandAvg.englishLanguagePercentage) * 100);
      if (Math.abs(diffPercent) >= 10) {
        if (diffPercent > 0) {
          parts.push(
            `Language requirements in ${metrics.location} show a higher English focus than average: around ${englishPct}% of tracked postings mention English (compared to ${formatPercent(finlandAvg.englishLanguagePercentage)}% nationally), ` +
              `while roughly ${localPct}% focus primarily on Finnish, Swedish or other local languages.`
          );
        } else {
          parts.push(
            `Language requirements in ${metrics.location} show a stronger local language focus: around ${englishPct}% of tracked postings mention English (compared to ${formatPercent(finlandAvg.englishLanguagePercentage)}% nationally), ` +
              `while roughly ${localPct}% focus primarily on Finnish, Swedish or other local languages.`
          );
        }
      } else {
        parts.push(
          `Language requirements in ${metrics.location} are similar to the national average: around ${englishPct}% of tracked postings mention English, ` +
            `while roughly ${localPct}% focus primarily on Finnish, Swedish or other local languages.`
        );
      }
    } else {
      parts.push(
        `Language requirements in ${metrics.location} are mixed: around ${englishPct}% of tracked postings mention English, ` +
          `while roughly ${localPct}% focus primarily on Finnish, Swedish or other local languages.`
      );
    }
  }

  return parts.join(' ');
}

function buildSalaryBlurbEn(metrics: LocationHeaderMetrics): string {
  if (!(metrics.avgSalaryNum > 0) || metrics.sampleCount <= 0) {
    return (
      `Salary insights for ${metrics.location} are based on a growing set of curated postings, ` +
      `and will improve as more employers publish detailed pay ranges.`
    );
  }

  const jobsCount = formatJobsCount(metrics.nbHits, 'en');
  const finlandAvg = metrics.finlandAverages;
  
  let comparisonText = '';
  if (finlandAvg?.avgSalaryNum && finlandAvg.avgSalaryNum > 0) {
    const diff = metrics.avgSalaryNum - finlandAvg.avgSalaryNum;
    const diffPercent = Math.round((diff / finlandAvg.avgSalaryNum) * 100);
    if (Math.abs(diffPercent) >= 5) {
      if (diffPercent > 0) {
        comparisonText = ` This is approximately ${Math.abs(diffPercent)}% higher than the Finland average of ${finlandAvg.avgSalaryFormatted}.`;
      } else {
        comparisonText = ` This is approximately ${Math.abs(diffPercent)}% lower than the Finland average of ${finlandAvg.avgSalaryFormatted}.`;
      }
    } else {
      comparisonText = ` This is similar to the Finland average of ${finlandAvg.avgSalaryFormatted}.`;
    }
  }

  return (
    `Our salary trends for ${metrics.location} are calculated from ${metrics.sampleCount.toLocaleString('en-US')} ` +
    `recent job postings with explicit pay information. The current typical advertised base pay is around ${metrics.avgSalaryFormatted} ` +
    `per month for full‑time roles.${comparisonText} We continuously refresh these figures as new job ads appear.`
  );
}

function buildWorkModelBlurbEn(metrics: LocationHeaderMetrics): string {
  const remotePct = formatPercent(metrics.remotePercentage);
  const finlandAvg = metrics.finlandAverages;
  
  if (remotePct <= 0) {
    let comparisonText = '';
    if (finlandAvg?.remotePercentage !== undefined && finlandAvg.remotePercentage > 0) {
      comparisonText = ` This is lower than the Finland average of ${formatPercent(finlandAvg.remotePercentage)}% remote or hybrid roles.`;
    }
    return (
      `Most roles in ${metrics.location} are currently advertised as on‑site positions, ` +
      `but hybrid and remote opportunities still appear in niche segments and senior roles.${comparisonText}`
    );
  }

  let flavour: string;
  if (remotePct >= 60) {
    flavour = `a strongly flexible market where remote and hybrid roles dominate new postings`;
  } else if (remotePct >= 40) {
    flavour = `a hybrid‑friendly market with a balanced mix of on‑site and flexible roles`;
  } else if (remotePct >= 20) {
    flavour = `a primarily on‑site market where remote or hybrid roles are available but competitive`;
  } else {
    flavour = `a mostly on‑site market where remote roles are still relatively rare`;
  }

  let comparisonText = '';
  if (finlandAvg?.remotePercentage !== undefined && finlandAvg.remotePercentage > 0) {
    const diff = remotePct - finlandAvg.remotePercentage;
    const diffPercent = Math.round((diff / finlandAvg.remotePercentage) * 100);
    if (Math.abs(diffPercent) >= 10) {
      if (diffPercent > 0) {
        comparisonText = ` This is significantly higher than the Finland average of ${formatPercent(finlandAvg.remotePercentage)}% remote or hybrid roles.`;
      } else {
        comparisonText = ` This is lower than the Finland average of ${formatPercent(finlandAvg.remotePercentage)}% remote or hybrid roles.`;
      }
    } else {
      comparisonText = ` This is similar to the Finland average of ${formatPercent(finlandAvg.remotePercentage)}% remote or hybrid roles.`;
    }
  }

  return (
    `Work‑model data for ${metrics.location} shows that approximately ${remotePct}% of tracked roles offer some form of remote or hybrid work, ` +
    `creating ${flavour}.${comparisonText} The remaining positions are advertised as fully on‑site, often in roles that require a physical presence.`
  );
}

function buildLanguageBlurbEn(metrics: LocationHeaderMetrics): string {
  const { english, local, total } = metrics.languageTotals;
  const finlandAvg = metrics.finlandAverages;
  
  if (total <= 0) {
    return (
      `Job postings in ${metrics.location} span both English‑speaking and local‑language roles, ` +
      `with requirements varying by company and job family.`
    );
  }

  const englishPct = formatPercent((english / total) * 100);
  const localPct = Math.max(0, 100 - englishPct);

  let comparisonText = '';
  if (finlandAvg?.englishLanguagePercentage !== undefined && finlandAvg.englishLanguagePercentage > 0) {
    const diff = englishPct - finlandAvg.englishLanguagePercentage;
    const diffPercent = Math.round((diff / finlandAvg.englishLanguagePercentage) * 100);
    if (Math.abs(diffPercent) >= 10) {
      if (diffPercent > 0) {
        comparisonText = ` This is higher than the Finland average of ${formatPercent(finlandAvg.englishLanguagePercentage)}% English requirements, `;
      } else {
        comparisonText = ` This is lower than the Finland average of ${formatPercent(finlandAvg.englishLanguagePercentage)}% English requirements, `;
      }
    } else {
      comparisonText = ` This is similar to the Finland average of ${formatPercent(finlandAvg.englishLanguagePercentage)}% English requirements, `;
    }
  }

  return (
    `Across the jobs we track in ${metrics.location}, roughly ${englishPct}% of postings list English as a required or primary language, ` +
    `while around ${localPct}% emphasise Finnish, Swedish or other local languages.${comparisonText}This mix gives international candidates and local talent ` +
    `clear options depending on whether they prefer English‑first workplaces or Finnish/Swedish‑speaking environments.`
  );
}

// TODO: We start with English‑focused blurbs; Finnish/Swedish use shorter,
// more generic variants that can be refined later.

function buildOverviewFi(metrics: LocationHeaderMetrics): string {
  const parts: string[] = [];

  if (metrics.location) {
    const companies = (metrics.topCompanies || []).filter(Boolean).map(cleanCompanyName);
    const industries = (metrics.topIndustries || []).filter(Boolean).slice(0, 3);

    if (companies.length > 0 || industries.length > 0) {
      let employersPhrase = '';
      if (companies.length > 0) {
        if (companies.length === 1) {
          employersPhrase = companies[0];
        } else if (companies.length === 2) {
          employersPhrase = `${companies[0]} ja ${companies[1]}`;
        } else {
          employersPhrase = `${companies[0]}, ${companies[1]} ja ${companies[2]}`;
        }
      }

      let industriesPhrase = '';
      if (industries.length > 0) {
        if (industries.length === 1) {
          industriesPhrase = industries[0];
        } else if (industries.length === 2) {
          industriesPhrase = `${industries[0]} ja ${industries[1]}`;
        } else {
          industriesPhrase = `${industries[0]}, ${industries[1]} ja ${industries[2]}`;
        }
      }

      if (employersPhrase && industriesPhrase) {
        parts.push(
          `JobCrawlsissa ${metrics.location} ‑alueelta löytyy tällä hetkellä avoimia työpaikkoja, ` +
            `suurimmat työnantajat ovat ${employersPhrase}, ja tärkeimmät toimialat kattavat ${industriesPhrase} sekä muita sektoreita.`
        );
      } else if (employersPhrase) {
        parts.push(
          `JobCrawlsissa ${metrics.location} ‑alueelta löytyy tällä hetkellä avoimia työpaikkoja, ` +
            `suurimmat työnantajat ovat ${employersPhrase} sekä monia muita yrityksiä.`
        );
      } else if (industriesPhrase) {
        parts.push(
          `JobCrawlsissa ${metrics.location} ‑alueelta löytyy tällä hetkellä avoimia työpaikkoja, ` +
            `tärkeimmät toimialat kattavat ${industriesPhrase} sekä muita sektoreita.`
        );
      } else {
        parts.push(
          `JobCrawlsissa ${metrics.location} ‑alueelta löytyy tällä hetkellä avoimia työpaikkoja eri toimialoilta ja yrityksiltä.`
        );
      }
    } else {
      parts.push(
        `JobCrawlsissa ${metrics.location} ‑alueelta löytyy tällä hetkellä avoimia työpaikkoja eri toimialoilta ja yrityksiltä.`
      );
    }
  }

  const finlandAvg = metrics.finlandAverages;
  if (metrics.avgSalaryNum > 0) {
    if (finlandAvg?.avgSalaryNum && finlandAvg.avgSalaryNum > 0) {
      const diff = metrics.avgSalaryNum - finlandAvg.avgSalaryNum;
      const diffPercent = Math.round((diff / finlandAvg.avgSalaryNum) * 100);
      if (Math.abs(diffPercent) >= 5) {
        if (diffPercent > 0) {
          parts.push(
            `Tyypillinen ilmoitettu kuukausipalkka on noin ${metrics.avgSalaryFormatted}, ` +
              `mikä on noin ${Math.abs(diffPercent)}% korkeampi kuin Suomen keskiarvo ${finlandAvg.avgSalaryFormatted}.`
          );
        } else {
          parts.push(
            `Tyypillinen ilmoitettu kuukausipalkka on noin ${metrics.avgSalaryFormatted}, ` +
              `mikä on noin ${Math.abs(diffPercent)}% alhaisempi kuin Suomen keskiarvo ${finlandAvg.avgSalaryFormatted}.`
          );
        }
      } else {
        parts.push(
          `Tyypillinen ilmoitettu kuukausipalkka on noin ${metrics.avgSalaryFormatted}, ` +
            `mikä on samanlainen kuin Suomen keskiarvo.`
        );
      }
    } else {
      parts.push(`Tyypillinen ilmoitettu kuukausipalkka on noin ${metrics.avgSalaryFormatted}, perustuen ilmoitettuihin palkkahaarukoihin.`);
    }
  }

  const { english, total } = metrics.languageTotals;
  if (total > 0) {
    const englishPct = formatPercent((english / total) * 100);
    if (finlandAvg?.englishLanguagePercentage !== undefined && finlandAvg.englishLanguagePercentage > 0) {
      const diff = englishPct - finlandAvg.englishLanguagePercentage;
      const diffPercent = Math.round((diff / finlandAvg.englishLanguagePercentage) * 100);
      if (Math.abs(diffPercent) >= 10) {
        if (diffPercent > 0) {
          parts.push(
            `Noin ${englishPct}% työpaikkailmoituksista mainitsee englannin (verrattuna ${formatPercent(finlandAvg.englishLanguagePercentage)}% kansalliseen keskiarvoon), ` +
              `loput painottuvat suomen, ruotsin tai muiden paikallisten kielten osaamiseen.`
          );
        } else {
          parts.push(
            `Noin ${englishPct}% työpaikkailmoituksista mainitsee englannin (verrattuna ${formatPercent(finlandAvg.englishLanguagePercentage)}% kansalliseen keskiarvoon), ` +
              `loput painottuvat suomen, ruotsin tai muiden paikallisten kielten osaamiseen.`
          );
        }
      } else {
        parts.push(
          `Noin ${englishPct}% työpaikkailmoituksista mainitsee englannin, mikä on samanlainen kuin kansallinen keskiarvo. ` +
            `Loput painottuvat suomen, ruotsin tai muiden paikallisten kielten osaamiseen.`
        );
      }
    } else {
      parts.push(
        `Noin ${englishPct}% työpaikkailmoituksista mainitsee englannin, loput painottuvat suomen, ruotsin tai muiden paikallisten kielten osaamiseen.`
      );
    }
  }

  return parts.join(' ');
}

function buildOverviewSv(metrics: LocationHeaderMetrics): string {
  const parts: string[] = [];

  if (metrics.location) {
    const companies = (metrics.topCompanies || []).filter(Boolean).map(cleanCompanyName);
    const industries = (metrics.topIndustries || []).filter(Boolean).slice(0, 3);

    if (companies.length > 0 || industries.length > 0) {
      let employersPhrase = '';
      if (companies.length > 0) {
        if (companies.length === 1) {
          employersPhrase = companies[0];
        } else if (companies.length === 2) {
          employersPhrase = `${companies[0]} och ${companies[1]}`;
        } else {
          employersPhrase = `${companies[0]}, ${companies[1]} och ${companies[2]}`;
        }
      }

      let industriesPhrase = '';
      if (industries.length > 0) {
        if (industries.length === 1) {
          industriesPhrase = industries[0];
        } else if (industries.length === 2) {
          industriesPhrase = `${industries[0]} och ${industries[1]}`;
        } else {
          industriesPhrase = `${industries[0]}, ${industries[1]} och ${industries[2]}`;
        }
      }

      if (employersPhrase && industriesPhrase) {
        parts.push(
          `På JobCrawls finns just nu lediga jobb i området ${metrics.location}, ` +
            `med de största arbetsgivarna ${employersPhrase} och de viktigaste branscherna ${industriesPhrase} samt andra sektorer.`
        );
      } else if (employersPhrase) {
        parts.push(
          `På JobCrawls finns just nu lediga jobb i området ${metrics.location}, ` +
            `med de största arbetsgivarna ${employersPhrase} och många fler.`
        );
      } else if (industriesPhrase) {
        parts.push(
          `På JobCrawls finns just nu lediga jobb i området ${metrics.location}, ` +
            `med de viktigaste branscherna ${industriesPhrase} samt andra sektorer.`
        );
      } else {
        parts.push(
          `På JobCrawls finns just nu lediga jobb i området ${metrics.location}, inom flera olika branscher och företag.`
        );
      }
    } else {
      parts.push(
        `På JobCrawls finns just nu lediga jobb i området ${metrics.location}, inom flera olika branscher och företag.`
      );
    }
  }

  const finlandAvg = metrics.finlandAverages;
  if (metrics.avgSalaryNum > 0) {
    if (finlandAvg?.avgSalaryNum && finlandAvg.avgSalaryNum > 0) {
      const diff = metrics.avgSalaryNum - finlandAvg.avgSalaryNum;
      const diffPercent = Math.round((diff / finlandAvg.avgSalaryNum) * 100);
      if (Math.abs(diffPercent) >= 5) {
        if (diffPercent > 0) {
          parts.push(
            `Den typiska annonserade månadslönen ligger runt ${metrics.avgSalaryFormatted}, ` +
              `vilket är cirka ${Math.abs(diffPercent)}% högre än Finlands genomsnitt på ${finlandAvg.avgSalaryFormatted}.`
          );
        } else {
          parts.push(
            `Den typiska annonserade månadslönen ligger runt ${metrics.avgSalaryFormatted}, ` +
              `vilket är cirka ${Math.abs(diffPercent)}% lägre än Finlands genomsnitt på ${finlandAvg.avgSalaryFormatted}.`
          );
        }
      } else {
        parts.push(
          `Den typiska annonserade månadslönen ligger runt ${metrics.avgSalaryFormatted}, ` +
            `vilket är liknande som Finlands genomsnitt.`
        );
      }
    } else {
      parts.push(`Den typiska annonserade månadslönen ligger runt ${metrics.avgSalaryFormatted} enligt aktuella platsannonser.`);
    }
  }

  const { english, total } = metrics.languageTotals;
  if (total > 0) {
    const englishPct = formatPercent((english / total) * 100);
    if (finlandAvg?.englishLanguagePercentage !== undefined && finlandAvg.englishLanguagePercentage > 0) {
      const diff = englishPct - finlandAvg.englishLanguagePercentage;
      const diffPercent = Math.round((diff / finlandAvg.englishLanguagePercentage) * 100);
      if (Math.abs(diffPercent) >= 10) {
        parts.push(
          `Cirka ${englishPct}% av jobben nämner engelska som arbetsspråk (jämfört med ${formatPercent(finlandAvg.englishLanguagePercentage)}% nationellt genomsnitt), ` +
            `medan resten främst kräver finska, svenska eller andra lokala språk.`
        );
      } else {
        parts.push(
          `Cirka ${englishPct}% av jobben nämner engelska som arbetsspråk, vilket är liknande som nationellt genomsnitt. ` +
            `Resten främst kräver finska, svenska eller andra lokala språk.`
        );
      }
    } else {
      parts.push(
        `Cirka ${englishPct}% av jobben nämner engelska som arbetsspråk, medan resten främst kräver finska, svenska eller andra lokala språk.`
      );
    }
  }

  return parts.join(' ');
}

function buildSalaryBlurbFi(metrics: LocationHeaderMetrics): string {
  if (!(metrics.avgSalaryNum > 0) || metrics.sampleCount <= 0) {
    return `Palkkatietoja kerätään parhaillaan ${metrics.location} ‑alueen ilmoituksista, ja tarkemmat luvut päivittyvät kun uusia palkkahaarukoita julkaistaan.`;
  }

  const finlandAvg = metrics.finlandAverages;
  let comparisonText = '';
  if (finlandAvg?.avgSalaryNum && finlandAvg.avgSalaryNum > 0) {
    const diff = metrics.avgSalaryNum - finlandAvg.avgSalaryNum;
    const diffPercent = Math.round((diff / finlandAvg.avgSalaryNum) * 100);
    if (Math.abs(diffPercent) >= 5) {
      if (diffPercent > 0) {
        comparisonText = ` Tämä on noin ${Math.abs(diffPercent)}% korkeampi kuin Suomen keskiarvo ${finlandAvg.avgSalaryFormatted}.`;
      } else {
        comparisonText = ` Tämä on noin ${Math.abs(diffPercent)}% alhaisempi kuin Suomen keskiarvo ${finlandAvg.avgSalaryFormatted}.`;
      }
    } else {
      comparisonText = ` Tämä on samanlainen kuin Suomen keskiarvo ${finlandAvg.avgSalaryFormatted}.`;
    }
  }

  return (
    `Palkkatrendit ${metrics.location} ‑alueella perustuvat ${metrics.sampleCount.toLocaleString('fi-FI')} työpaikkailmoitukseen, ` +
    `joissa palkka on ilmoitettu avoimesti. Nykyinen tyypillinen ilmoitettu kuukausipalkka on noin ${metrics.avgSalaryFormatted}.${comparisonText} ` +
    `Luvut päivittyvät jatkuvasti uusien ilmoitusten myötä.`
  );
}

function buildWorkModelBlurbFi(metrics: LocationHeaderMetrics): string {
  const remotePct = formatPercent(metrics.remotePercentage);
  const finlandAvg = metrics.finlandAverages;

  if (remotePct <= 0) {
    let comparisonText = '';
    if (finlandAvg?.remotePercentage !== undefined && finlandAvg.remotePercentage > 0) {
      comparisonText = ` Tämä on alhaisempi kuin Suomen keskiarvo ${formatPercent(finlandAvg.remotePercentage)}% etä‑ tai hybridityörooleista.`;
    }
    return `Suurin osa työpaikoista ${metrics.location} ‑alueella on tällä hetkellä paikan päällä tehtävää työtä, vaikka yksittäisiä etä‑ ja hybridityömahdollisuuksia löytyykin.${comparisonText}`;
  }

  let comparisonText = '';
  if (finlandAvg?.remotePercentage !== undefined && finlandAvg.remotePercentage > 0) {
    const diff = remotePct - finlandAvg.remotePercentage;
    const diffPercent = Math.round((diff / finlandAvg.remotePercentage) * 100);
    if (Math.abs(diffPercent) >= 10) {
      if (diffPercent > 0) {
        comparisonText = ` Tämä on merkittävästi korkeampi kuin Suomen keskiarvo ${formatPercent(finlandAvg.remotePercentage)}% etä‑ tai hybridityörooleista.`;
      } else {
        comparisonText = ` Tämä on alhaisempi kuin Suomen keskiarvo ${formatPercent(finlandAvg.remotePercentage)}% etä‑ tai hybridityörooleista.`;
      }
    } else {
      comparisonText = ` Tämä on samanlainen kuin Suomen keskiarvo ${formatPercent(finlandAvg.remotePercentage)}% etä‑ tai hybridityörooleista.`;
    }
  }

  return (
    `Työmallidatan perusteella noin ${remotePct}% ${metrics.location} ‑alueen työpaikoista tarjoaa etä‑ tai hybridityömahdollisuuden, ` +
    `muiden roolien painottuessa lähityöhön toimistolla tai asiakaskohteissa.${comparisonText}`
  );
}

function buildLanguageBlurbFi(metrics: LocationHeaderMetrics): string {
  const { english, total } = metrics.languageTotals;
  const finlandAvg = metrics.finlandAverages;

  if (total <= 0) {
    return `Kielivaatimukset vaihtelevat työpaikoittain; osa rooleista on englanninkielisiä ja osa vaatii suomen‑ tai ruotsinkielistä osaamista.`;
  }

  const englishPct = formatPercent((english / total) * 100);
  let comparisonText = '';
  if (finlandAvg?.englishLanguagePercentage !== undefined && finlandAvg.englishLanguagePercentage > 0) {
    const diff = englishPct - finlandAvg.englishLanguagePercentage;
    const diffPercent = Math.round((diff / finlandAvg.englishLanguagePercentage) * 100);
    if (Math.abs(diffPercent) >= 10) {
      if (diffPercent > 0) {
        comparisonText = ` Tämä on korkeampi kuin Suomen keskiarvo ${formatPercent(finlandAvg.englishLanguagePercentage)}% englannin vaatimuksista.`;
      } else {
        comparisonText = ` Tämä on alhaisempi kuin Suomen keskiarvo ${formatPercent(finlandAvg.englishLanguagePercentage)}% englannin vaatimuksista.`;
      }
    } else {
      comparisonText = ` Tämä on samanlainen kuin Suomen keskiarvo ${formatPercent(finlandAvg.englishLanguagePercentage)}% englannin vaatimuksista.`;
    }
  }

  return (
    `Seuraamissamme ilmoituksissa noin ${englishPct}% mainitsee englannin vaadittuna kielenä, ` +
    `ja loput korostavat suomen, ruotsin tai muiden paikallisten kielten osaamista.${comparisonText}`
  );
}

function buildSalaryBlurbSv(metrics: LocationHeaderMetrics): string {
  if (!(metrics.avgSalaryNum > 0) || metrics.sampleCount <= 0) {
    return `Lönestatistik för ${metrics.location} byggs upp allt eftersom fler arbetsgivare anger löneintervall i sina platsannonser.`;
  }

  const finlandAvg = metrics.finlandAverages;
  let comparisonText = '';
  if (finlandAvg?.avgSalaryNum && finlandAvg.avgSalaryNum > 0) {
    const diff = metrics.avgSalaryNum - finlandAvg.avgSalaryNum;
    const diffPercent = Math.round((diff / finlandAvg.avgSalaryNum) * 100);
    if (Math.abs(diffPercent) >= 5) {
      if (diffPercent > 0) {
        comparisonText = ` Detta är cirka ${Math.abs(diffPercent)}% högre än Finlands genomsnitt på ${finlandAvg.avgSalaryFormatted}.`;
      } else {
        comparisonText = ` Detta är cirka ${Math.abs(diffPercent)}% lägre än Finlands genomsnitt på ${finlandAvg.avgSalaryFormatted}.`;
      }
    } else {
      comparisonText = ` Detta är liknande som Finlands genomsnitt på ${finlandAvg.avgSalaryFormatted}.`;
    }
  }

  return (
    `Lönetrenderna för ${metrics.location} baseras på ${metrics.sampleCount.toLocaleString('sv-SE')} aktuella annonser ` +
    `där lön anges öppet. Den typiska annonserade månadslönen ligger runt ${metrics.avgSalaryFormatted}.${comparisonText} ` +
    `Siffrorna uppdateras kontinuerligt när nya annonser publiceras.`
  );
}

function buildWorkModelBlurbSv(metrics: LocationHeaderMetrics): string {
  const remotePct = formatPercent(metrics.remotePercentage);
  const finlandAvg = metrics.finlandAverages;

  if (remotePct <= 0) {
    let comparisonText = '';
    if (finlandAvg?.remotePercentage !== undefined && finlandAvg.remotePercentage > 0) {
      comparisonText = ` Detta är lägre än Finlands genomsnitt på ${formatPercent(finlandAvg.remotePercentage)}% hybrid‑ eller distansroller.`;
    }
    return `De flesta jobb i ${metrics.location} annonseras som arbete på plats, men enstaka hybrid‑ och distansroller förekommer.${comparisonText}`;
  }

  let comparisonText = '';
  if (finlandAvg?.remotePercentage !== undefined && finlandAvg.remotePercentage > 0) {
    const diff = remotePct - finlandAvg.remotePercentage;
    const diffPercent = Math.round((diff / finlandAvg.remotePercentage) * 100);
    if (Math.abs(diffPercent) >= 10) {
      if (diffPercent > 0) {
        comparisonText = ` Detta är betydligt högre än Finlands genomsnitt på ${formatPercent(finlandAvg.remotePercentage)}% hybrid‑ eller distansroller.`;
      } else {
        comparisonText = ` Detta är lägre än Finlands genomsnitt på ${formatPercent(finlandAvg.remotePercentage)}% hybrid‑ eller distansroller.`;
      }
    } else {
      comparisonText = ` Detta är liknande som Finlands genomsnitt på ${formatPercent(finlandAvg.remotePercentage)}% hybrid‑ eller distansroller.`;
    }
  }

  return (
    `Arbetsmodellsdatan visar att ungefär ${remotePct}% av jobben i ${metrics.location} erbjuder hybrid‑ eller distansarbete, ` +
    `medan resten huvudsakligen är tjänster på plats hos arbetsgivaren eller kund.${comparisonText}`
  );
}

function buildLanguageBlurbSv(metrics: LocationHeaderMetrics): string {
  const { english, total } = metrics.languageTotals;
  const finlandAvg = metrics.finlandAverages;

  if (total <= 0) {
    return `Språkkraven varierar mellan engelska och lokala språk beroende på bransch, arbetsgivare och roll.`;
  }

  const englishPct = formatPercent((english / total) * 100);
  let comparisonText = '';
  if (finlandAvg?.englishLanguagePercentage !== undefined && finlandAvg.englishLanguagePercentage > 0) {
    const diff = englishPct - finlandAvg.englishLanguagePercentage;
    const diffPercent = Math.round((diff / finlandAvg.englishLanguagePercentage) * 100);
    if (Math.abs(diffPercent) >= 10) {
      if (diffPercent > 0) {
        comparisonText = ` Detta är högre än Finlands genomsnitt på ${formatPercent(finlandAvg.englishLanguagePercentage)}% engelska krav.`;
      } else {
        comparisonText = ` Detta är lägre än Finlands genomsnitt på ${formatPercent(finlandAvg.englishLanguagePercentage)}% engelska krav.`;
      }
    } else {
      comparisonText = ` Detta är liknande som Finlands genomsnitt på ${formatPercent(finlandAvg.englishLanguagePercentage)}% engelska krav.`;
    }
  }

  return (
    `Bland de jobb vi följer i ${metrics.location} nämner omkring ${englishPct}% engelska som arbetsspråk, ` +
    `medan övriga främst kräver finska, svenska eller andra lokala språk.${comparisonText}`
  );
}

/**
 * Main entrypoint: generate all header blurbs for a given location + metrics.
 */
export function generateLocationMarketHeaderText(
  metrics: LocationHeaderMetrics
): LocationHeaderText {
  const locale: SupportedLocale = metrics.locale || 'en';

  if (locale === 'fi') {
    return {
      overviewText: buildOverviewFi(metrics),
      salaryBlurb: buildSalaryBlurbFi(metrics),
      workModelBlurb: buildWorkModelBlurbFi(metrics),
      languageBlurb: buildLanguageBlurbFi(metrics),
    };
  }

  if (locale === 'sv') {
    return {
      overviewText: buildOverviewSv(metrics),
      salaryBlurb: buildSalaryBlurbSv(metrics),
      workModelBlurb: buildWorkModelBlurbSv(metrics),
      languageBlurb: buildLanguageBlurbSv(metrics),
    };
  }

  // Default: English
  return {
    overviewText: buildOverviewEn(metrics),
    salaryBlurb: buildSalaryBlurbEn(metrics),
    workModelBlurb: buildWorkModelBlurbEn(metrics),
    languageBlurb: buildLanguageBlurbEn(metrics),
  };
}

